import {
  type CharterPartyTerms,
  type CpExclusion,
  type CpStatus,
  type CpType,
  currentCharterParty,
  type PassageClass,
  parseCharterParty,
} from '@felix/domain';
import { formatDateTime } from '../../format.js';
import type { StatusTone } from '../../StatusBadge.js';
import type { CharterPartyItem, HireReport, VoyageReport } from './types.js';

/**
 * Charter party formunun ve register'ın yardımcıları (ADR 0084 §7). Doğrulama sunucudaki şemayla
 * aynıdır (`parseCharterParty`); hata kodları alan adına eşlenir. Saf fonksiyonlar.
 */

export interface CpFormValues {
  charterer: string;
  cpNo: string;
  cpDate: string | null;
  cpType: CpType;
  deliveryPlace: string;
  /** UTC, `YYYY-MM-DDTHH:mm`; boşsa `''`. */
  deliveryAt: string;
  deliveryReportId: string | null;
  redeliveryPlace: string;
  redeliveryAt: string;
  redeliveryReportId: string | null;
  speedLadenKn: number | null;
  speedBallastKn: number | null;
  consumptionLadenMt: number | null;
  consumptionBallastMt: number | null;
  mainFuel: string | null;
  auxFuel: string | null;
  speedToleranceKn: number | null;
  consumptionTolerancePct: number | null;
  maxBeaufort: number | null;
  maxDouglas: number | null;
  maxWaveHeightM: number | null;
  maxSwellM: number | null;
  minDayHours: number | null;
  currentClause: string;
  exclusions: CpExclusion[];
  notes: string;
}

export const CP_FORM_EMPTY: CpFormValues = {
  charterer: '',
  cpNo: '',
  cpDate: null,
  cpType: 'time',
  deliveryPlace: '',
  deliveryAt: '',
  deliveryReportId: null,
  redeliveryPlace: '',
  redeliveryAt: '',
  redeliveryReportId: null,
  speedLadenKn: null,
  speedBallastKn: null,
  consumptionLadenMt: null,
  consumptionBallastMt: null,
  mainFuel: null,
  auxFuel: null,
  speedToleranceKn: null,
  consumptionTolerancePct: null,
  maxBeaufort: null,
  maxDouglas: null,
  maxWaveHeightM: null,
  maxSwellM: null,
  minDayHours: null,
  currentClause: '',
  exclusions: [],
  notes: '',
};

/** UTC an → form değeri `YYYY-MM-DDTHH:mm`. */
export function toUtcInput(iso: string | null | undefined): string {
  return iso ? new Date(iso).toISOString().slice(0, 16) : '';
}

/** Form değeri (UTC) → an; boşsa `null`. */
export function fromUtcInput(value: string): string | null {
  return value ? new Date(`${value}:00.000Z`).toISOString() : null;
}

export function cpFormValues(item: CharterPartyItem | null): CpFormValues {
  if (!item) return { ...CP_FORM_EMPTY, exclusions: [] };
  const t = item.terms;
  return {
    charterer: item.charterer ?? '',
    cpNo: item.cpNo ?? '',
    cpDate: item.cpDate,
    cpType: item.cpType,
    deliveryPlace: t.deliveryPlace ?? '',
    deliveryAt: toUtcInput(item.deliveryAt),
    deliveryReportId: item.deliveryReportId,
    redeliveryPlace: t.redeliveryPlace ?? '',
    redeliveryAt: toUtcInput(item.redeliveryAt),
    redeliveryReportId: item.redeliveryReportId,
    speedLadenKn: t.speedLadenKn,
    speedBallastKn: t.speedBallastKn,
    consumptionLadenMt: t.consumptionLadenMt,
    consumptionBallastMt: t.consumptionBallastMt,
    mainFuel: t.mainFuel,
    auxFuel: t.auxFuel,
    speedToleranceKn: t.speedToleranceKn,
    consumptionTolerancePct: t.consumptionTolerancePct,
    maxBeaufort: t.maxBeaufort,
    maxDouglas: t.maxDouglas,
    maxWaveHeightM: t.maxWaveHeightM,
    maxSwellM: t.maxSwellM,
    minDayHours: t.minDayHours,
    currentClause: t.currentClause ?? '',
    exclusions: [...t.exclusions],
    notes: t.notes ?? '',
  };
}

const text = (value: string) => (value.trim() ? value.trim() : null);

/** Formdan sunucuya giden içerik (`charterPartySchema` girdisi). */
export function cpContentOf(values: CpFormValues) {
  const terms: CharterPartyTerms = {
    deliveryPlace: text(values.deliveryPlace),
    redeliveryPlace: text(values.redeliveryPlace),
    speedLadenKn: values.speedLadenKn,
    speedBallastKn: values.speedBallastKn,
    consumptionLadenMt: values.consumptionLadenMt,
    consumptionBallastMt: values.consumptionBallastMt,
    mainFuel: values.mainFuel as CharterPartyTerms['mainFuel'],
    auxFuel: values.auxFuel as CharterPartyTerms['auxFuel'],
    speedToleranceKn: values.speedToleranceKn,
    consumptionTolerancePct: values.consumptionTolerancePct,
    maxBeaufort: values.maxBeaufort,
    maxDouglas: values.maxDouglas,
    maxWaveHeightM: values.maxWaveHeightM,
    maxSwellM: values.maxSwellM,
    currentClause: text(values.currentClause),
    minDayHours: values.minDayHours,
    exclusions: [...values.exclusions],
    notes: text(values.notes),
  };
  return {
    charterer: text(values.charterer),
    cpNo: text(values.cpNo),
    cpDate: values.cpDate,
    cpType: values.cpType,
    deliveryReportId: values.deliveryReportId,
    redeliveryReportId: values.redeliveryReportId,
    deliveryAt: fromUtcInput(values.deliveryAt),
    redeliveryAt: fromUtcInput(values.redeliveryAt),
    terms,
  };
}

/** Alan başına ilk hata kodu (sunucunun şemasıyla; `terms.` öneki alan adından atılır). */
export function cpFormErrors(values: CpFormValues): Record<string, string> {
  const parsed = parseCharterParty(cpContentOf(values), { source: 'office' });
  if (parsed.ok) return {};
  const out: Record<string, string> = {};
  for (const issue of parsed.issues) {
    const field = issue.path.replace(/^terms\./, '').split('.')[0] ?? issue.path;
    out[field] ??= issue.code;
  }
  return out;
}

/** On Hire / Off Hire seçeneğinin etiketi: numara, zaman (UTC), CP no. */
export function hireReportLabel(report: HireReport): string {
  return [report.reportNo, `${formatDateTime(report.reportedAt, 0)} UTC`, report.charterPartyNo]
    .filter(Boolean)
    .join(' · ');
}

export const CP_STATUS_TONE: Record<CpStatus, StatusTone> = {
  planned: 'info',
  on_hire: 'success',
  redelivered: 'neutral',
};

export const PASSAGE_CLASS_TONE: Record<PassageClass, StatusTone> = {
  owners_account: 'info',
  awaiting_employment: 'warning',
  unclassified: 'neutral',
};

/* ---------- Kira yönlendirmesi (ADR 0157 §7) ---------- */

/** Kalkışın kira bilgisi: istihdam ve referans (Classification). */
function classificationOf(report: VoyageReport): { employment: unknown; reference: string | null } {
  const value = (report.data.classification ?? {}) as Record<string, unknown>;
  const reference =
    typeof value.charterReference === 'string' && value.charterReference.trim()
      ? value.charterReference.trim()
      : null;
  return { employment: value.employment, reference };
}

/**
 * "Kalkışta kira bildirildi, CP kaydı yok" (ADR 0157 §7, #2273): gönderilmiş, iptal edilmemiş,
 * "Kiralı" kalkışlardan hiçbir CP döneminin kapsamadığı. CP'yi ofis açar (ADR 0084 §1); kalkıştaki
 * bilgi yalnız sınıflamadır, kayıt açmaz. Tarih sırasıyla.
 */
export function charterWithoutCp(
  reports: readonly VoyageReport[],
  items: readonly CharterPartyItem[],
): { report: VoyageReport; reference: string | null }[] {
  return reports
    .filter(
      (r) =>
        r.kind === 'departure' &&
        r.status === 'submitted' &&
        classificationOf(r).employment === 'chartered' &&
        currentCharterParty(items, r.reportedAt) === null,
    )
    .sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt))
    .map((report) => ({ report, reference: classificationOf(report).reference }));
}

/**
 * "Yeni CP" ön doldurma (ADR 0157 §7): kiracı = kalkışın referansı, planlanan teslim = kalkış
 * saati (UTC). Hepsi düzenlenebilir; teslim, gemi On Hire'ı gönderince o raporla değiştirilir.
 */
export function newCpPrefill(departure: VoyageReport): CpFormValues {
  return {
    ...CP_FORM_EMPTY,
    exclusions: [],
    charterer: classificationOf(departure).reference ?? '',
    deliveryAt: toUtcInput(departure.reportedAt),
  };
}

/**
 * CP'ye bağlı olmayan Kiraya Giriş raporları (ADR 0157 §7): gönderilmiş On Hire'lardan hiçbir
 * CP'nin teslim raporu olmayanlar. Ofis CP'de teslim olarak seçer (ADR 0084 §2).
 */
export function unlinkedOnHire(
  hireReports: readonly HireReport[],
  items: readonly Pick<CharterPartyItem, 'deliveryReportId'>[],
): HireReport[] {
  const used = new Set(items.map((i) => i.deliveryReportId).filter(Boolean));
  return hireReports.filter(
    (h) => h.kind === 'on_hire' && h.status === 'submitted' && !used.has(h.id),
  );
}
